import './App.css';
import { Routes, Route, useParams, Navigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { supabase } from './utils/supabase';
import LoginPage from './pages/LoginPage';
import MagicLoginPage from './pages/MagicLoginPage';
import AdminClientsPage from './pages/AdminClientsPage';
import AdminCoursesPage from './pages/AdminCoursesPage';
import AdminBootcampsCalendar from './pages/AdminBootcampsCalendar';
import AdminAnalyticsPage from './pages/AdminAnalyticsPage';
import AdminClientPage from './pages/AdminClientPage';
import AdminRegionPage from './pages/AdminRegionPage';
import CourseDetail from './pages/CourseDetail';
import TeamEditMembers from './pages/TeamEditMembers';
import ManageAccess from './pages/ManageAccess';
import BootcampDetail from './pages/BootcampDetail';
import BootcampUserDetail from './pages/BootcampUserDetail';
import ManagerDashboard from './pages/ManagerDashboard';
import RenderMonitor from './components/RenderMonitor';
import ErrorBoundary from './components/ErrorBoundary';
import TeamBootcamps from './pages/TeamBootcamps';
import InactiveTeams from './pages/InactiveTeams';
import ClientBootcamps from './pages/ClientBootcamps';
import ArchivedCourses from './pages/ArchivedCourses';
import NotAuthorized from './pages/NotAuthorized';
import PropelionAIPage from './pages/PropelionAIPage';
import CourseAIPage from './pages/CourseAIPage';
import ReflectionAITestPage from './pages/ReflectionAITestPage';
import DashboardLayout from './layouts/DashboardLayout';
import { getEffectiveIsGlobal } from './utils/adminViewMode';

function AdminClientPageWrapper() {
  return <AdminClientPage />;
}

function AdminRegionPageWrapper() {
  return <AdminRegionPage />;
}

function CourseDetailWrapper() {
  const { id } = useParams();
  return <CourseDetail id={id} />;
}

function CourseAIWrapper() {
  const { id } = useParams();
  return <CourseAIPage courseId={id} />;
}

function ReflectionAITestWrapper() {
  const { id, questionId } = useParams();
  return <ReflectionAITestPage courseId={id} questionId={questionId} />;
}

function TeamEditMembersWrapper() {
  const { id } = useParams();
  return <TeamEditMembers id={id} />;
}

function BootcampDetailWrapper() {
  const { id } = useParams();
  return <BootcampDetail id={id} />;
}

function BootcampUserDetailWrapper() {
  return <BootcampUserDetail />;
}

function BootcampReflectionsWrapper() {
  const { id } = useParams();
  return <Navigate to={`/bootcamp/${id}#reflections`} replace />;
}

function ClientViewerRedirect() {
  const { id } = useParams();
  return <Navigate to={`/manager?client=${id}`} replace />;
}

function ProtectedRoute({ children, allowRestrictedOnly = false, allowGlobalOnly = false }) {
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);
  const [checkingAdmin, setCheckingAdmin] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isGlobal, setIsGlobal] = useState(false);
  const location = useLocation();
  const effectiveIsGlobal = getEffectiveIsGlobal(isGlobal);

  useEffect(() => {
    let mounted = true;
    const setup = async () => {
      const { data: sessionData } = await supabase.auth.getSession();
      if (!mounted) return;
      const currentSession = sessionData?.session || null;
      setSession(currentSession);
      setLoading(false);
      if (currentSession?.user?.id) {
        try {
          const userId = currentSession.user.id;
          const { data: globalRow } = await supabase
            .from('lean_admin_users')
            .select('user_id, is_global')
            .eq('user_id', userId)
            .eq('is_global', true)
            .maybeSingle();
          let allowed = !!globalRow;
          setIsGlobal(!!globalRow);
          if (!allowed) {
            const [{ data: cli }, { data: reg }] = await Promise.all([
              supabase.from('admin_user_clients').select('user_id').eq('user_id', userId).limit(1),
              supabase.from('admin_user_regions').select('user_id').eq('user_id', userId).limit(1),
            ]);
            allowed = (cli && cli.length > 0) || (reg && reg.length > 0);
          }
          if (!mounted) return;
          setIsAdmin(allowed);
          setCheckingAdmin(false);
        } catch (e) {
          if (!mounted) return;
          setIsAdmin(false);
          setCheckingAdmin(false);
        }
      } else {
        setCheckingAdmin(false);
      }
    };
    setup();
    const { data: listener } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession);
    });
    return () => { mounted = false; listener?.subscription?.unsubscribe(); };
  }, []);

  if (loading || checkingAdmin) return null;
  if (!session) return <Navigate to="/login" state={{ from: location }} replace />;
  if (!isAdmin) return <Navigate to="/not-authorized" replace />;
  if (allowRestrictedOnly && effectiveIsGlobal) return <Navigate to="/not-authorized" replace />;
  if (allowGlobalOnly && !effectiveIsGlobal) return <Navigate to="/not-authorized" replace />;
  return children;
}


function App() {
  return (
    <ErrorBoundary>
      <div className="App">
        <RenderMonitor componentName="App" />
        <Routes>
          <Route path="/login" element={<MagicLoginPage />} />
          <Route path="/admin-login" element={<LoginPage />} />
          <Route path="/not-authorized" element={<NotAuthorized />} />

          <Route element={<DashboardLayout />}>
            <Route path="/admin" element={<ProtectedRoute allowGlobalOnly><Navigate to="/admin/clients" replace /></ProtectedRoute>} />
            <Route path="/admin/clients" element={<ProtectedRoute allowGlobalOnly><AdminClientsPage /></ProtectedRoute>} />
            <Route path="/admin/courses" element={<ProtectedRoute allowGlobalOnly><AdminCoursesPage /></ProtectedRoute>} />
            <Route path="/admin/bootcamps" element={<ProtectedRoute allowGlobalOnly><AdminBootcampsCalendar /></ProtectedRoute>} />
            <Route path="/admin/analytics" element={<ProtectedRoute allowGlobalOnly><AdminAnalyticsPage /></ProtectedRoute>} />
            <Route path="/propelion-ai" element={<ProtectedRoute allowGlobalOnly><PropelionAIPage /></ProtectedRoute>} />
            <Route path="/archived-courses" element={<ProtectedRoute allowGlobalOnly><ArchivedCourses /></ProtectedRoute>} />
            <Route path="/client/:id" element={<ProtectedRoute allowGlobalOnly><AdminClientPageWrapper /></ProtectedRoute>} />
            <Route path="/client/:clientId/region/:regionId" element={<ProtectedRoute allowGlobalOnly><AdminRegionPageWrapper /></ProtectedRoute>} />
            <Route path="/client/:id/view" element={<ProtectedRoute allowRestrictedOnly><ClientViewerRedirect /></ProtectedRoute>} />
            <Route path="/manager" element={<ProtectedRoute allowRestrictedOnly><ManagerDashboard /></ProtectedRoute>} />
            <Route path="/client/:id/access" element={<ProtectedRoute allowGlobalOnly><ManageAccess /></ProtectedRoute>} />
            <Route path="/client/:id/bootcamps" element={<ProtectedRoute allowGlobalOnly><ClientBootcamps /></ProtectedRoute>} />
            <Route path="/client/:id/inactive-teams" element={<ProtectedRoute allowGlobalOnly><InactiveTeams /></ProtectedRoute>} />
            <Route path="/course/:id" element={<ProtectedRoute allowGlobalOnly><CourseDetailWrapper /></ProtectedRoute>} />
            <Route path="/course/:id/ai" element={<ProtectedRoute allowGlobalOnly><CourseAIWrapper /></ProtectedRoute>} />
            <Route path="/course/:id/reflection/:questionId/test-ai" element={<ProtectedRoute allowGlobalOnly><ReflectionAITestWrapper /></ProtectedRoute>} />
            <Route path="/team/:id/edit-members" element={<ProtectedRoute allowGlobalOnly><TeamEditMembersWrapper /></ProtectedRoute>} />
            <Route path="/region/:id/access" element={<ProtectedRoute allowGlobalOnly><ManageAccess /></ProtectedRoute>} />
            <Route path="/team/:id/bootcamps" element={<ProtectedRoute allowGlobalOnly><TeamBootcamps /></ProtectedRoute>} />
            <Route path="/bootcamp/:id" element={<ProtectedRoute><BootcampDetailWrapper /></ProtectedRoute>} />
            <Route path="/bootcamp/:id/user/:userId" element={<ProtectedRoute><BootcampUserDetailWrapper /></ProtectedRoute>} />
            <Route path="/bootcamp/:id/reflections" element={<ProtectedRoute><BootcampReflectionsWrapper /></ProtectedRoute>} />
          </Route>

          <Route path="/" element={<MagicLoginPage />} />
        </Routes>
      </div>
    </ErrorBoundary>
  );
}

export default App;
